/**
 * Grand Quack Auto — floating score popups.
 *
 * CSS-animated text overlays ("+10", "MEGA QUACK!") that float upward and fade.
 * Called by chaosSystem when addChaos fires.
 *
 * See research.md §2 "floating score popups".
 */

import type { World } from '../types';

const POPUP_LABELS: Record<string, string> = {
  scare: '+10 QUACK!',
  bump: '+5',
  copHit: '+45 COP HIT!',
  knockdown: '+18 KNOCKDOWN!',
  vehicleDestroy: '+20 WRECKED!',
  ducklingDown: 'DUCKLING DOWN! 🐥💔',
  ducklingHatch: '+1 🐥 DUCKLING!',
  motherRage: '🔥 MOTHER\'S RAGE!',
  launched: '🚀 LAUNCHED!',
};

const POPUP_COLORS: Record<string, string> = {
  scare: '#ffdd00',
  bump: '#ffffff',
  copHit: '#ff4444',
  knockdown: '#ff8822',
  vehicleDestroy: '#ff6600',
  ducklingDown: '#ff2244',
  ducklingHatch: '#ffd700',
  motherRage: '#ff3300',
  launched: '#ff8800',
};

/**
 * Trigger a full-width gold banner that sweeps left → right behind the
 * combo HUD text. Used by comboSystem at x8 / x10 tier crossings.
 * Self-removes when the CSS animation ends.
 */
export function triggerComboBannerSweep(label: string): void {
  const hud = document.getElementById('hud');
  if (!hud) return;
  const el = document.createElement('div');
  el.className = 'combo-banner-sweep';
  el.textContent = label;
  hud.appendChild(el);
  el.addEventListener('animationend', () => el.remove());
  setTimeout(() => el.remove(), 1500);
}

/**
 * Show a floating score popup at the duck's screen position.
 * The popup animates upward and fades via CSS animation, then self-removes.
 */
export function showPopup(world: World, kind: string): void {
  const hud = document.getElementById('hud');
  if (!hud) return;

  const label = POPUP_LABELS[kind] ?? `+${kind}`;
  const color = POPUP_COLORS[kind] ?? '#ffffff';

  // Project duck position to screen space.
  const pos = world.duck.position.clone();
  pos.y += 1.5;
  pos.project(world.camera);

  const x = (pos.x * 0.5 + 0.5) * window.innerWidth;
  const y = (-pos.y * 0.5 + 0.5) * window.innerHeight;

  const el = document.createElement('div');
  el.className = 'score-popup';
  el.textContent = label;
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  el.style.color = color;
  hud.appendChild(el);

  // Self-remove after animation completes.
  el.addEventListener('animationend', () => el.remove());
  setTimeout(() => el.remove(), 1200); // fallback cleanup
}
